// The bare i18next singleton, not '@/lib/i18n'. Same instance (that module only
// configures this one), but importing it here keeps checkout.ts free of the
// locale bundle and of any import cycle with the i18n setup.
import i18n from 'i18next';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { isGerman } from './language';
import { trackConversion } from './analytics';
import { requestB2BConfirmation } from './b2bConfirmGate';
import { SUPPORT_EMAIL } from './plans';

/**
 * R2-10: the displayed currency used to follow `navigator.language`, so a DACH
 * user who switched the app to German but ran an English browser saw a German
 * UI with USD prices. The chosen app language now decides; the browser language
 * is only the fallback for the moment before i18n has resolved a language.
 *
 * Read fresh on every call (never cached at module load) so switching the
 * language mid-session takes effect immediately.
 */
export const getCurrency = (): 'eur' | 'usd' => {
  const lang = i18n.language || navigator.language || 'de';
  return isGerman(lang) ? 'eur' : 'usd';
};

/** Einzige Stelle, die aus dem Checkout-Pfad wegnavigiert, in Tests ersetzbar. */
export const checkoutNavigation = {
  assign(url: string): void {
    window.location.href = url;
  },
};

/** `cap5000_monthly` wird zu `cap5000`. */
export function bandTierFromLookup(lookupKey: string): string {
  return lookupKey.replace(/_(monthly|yearly)$/, '');
}

type CheckoutErrorKind = 'active' | 'special' | 'rateLimited' | 'other';

/** Liest Status und Body eines FunctionsHttpError (Muster lib/manageSubscription.ts). */
async function classifyCheckoutError(error: unknown): Promise<CheckoutErrorKind> {
  const ctx = (error as { context?: Response })?.context;
  if (!ctx || typeof ctx.json !== 'function') return 'other';
  if (ctx.status === 429) return 'rateLimited';
  try {
    const body = await (typeof ctx.clone === 'function' ? ctx.clone() : ctx).json();
    if (body?.code === 'ACTIVE_SUBSCRIPTION_EXISTS') return 'active';
    if (body?.code === 'SPECIAL_PLAN') return 'special';
  } catch {
    // kein JSON-Body
  }
  return 'other';
}

const checkoutText = (key: string) => i18n.t(`checkout:${key}`, { email: SUPPORT_EMAIL });

/**
 * Startet den Stripe-Checkout fuer `lookupKey`. Das Abrechnungsintervall
 * steckt im Lookup-Key selbst (`capX_monthly` / `capX_yearly`) und wird vom
 * AUFRUFER entschieden (`defaultBillingCycle` in lib/plans.ts).
 *
 * UX-Audit A7O-04 (26.09.2026): wirft NIE. Jeder Fehler endet in einem Toast
 * mit Ausweg, erwartete Zustaende (Sondertarif, Rate-Limit) ohne Sentry.
 * Vorher warf die Funktion, und alle sechs Aufrufer fingen nur fuer Sentry ab:
 * Dialog zu, Spinner weg, kein Wort. Rueckgabe { redirected: true } heisst
 * "der Browser navigiert weg", der Spinner darf stehen bleiben.
 *
 * B2B-Gate (Paket B Umsatzsteuer) steht unveraendert VOR allem anderen; ein
 * Abbruch dort ist kein Fehler und liefert { redirected: false } ohne Toast.
 */
export const handleUpgrade = async (
  lookupKey: string,
  currency: 'eur' | 'usd' = getCurrency(),
): Promise<{ redirected: boolean }> => {
  try {
    // A7-NEU-2: der Dialog nennt, was gebucht wird.
    const confirmed = await requestB2BConfirmation({ lookupKey, currency });
    if (!confirmed) return { redirected: false };

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { lookupKey, currency, b2b_confirmed: true },
    });

    if (error) {
      const kind = await classifyCheckoutError(error);
      if (kind === 'active') {
        // Doppel-Abo-Guard des Servers: Bestandskunden wechseln das Band auf
        // der Preisseite, das gewaehlte Band ist dort vorgewaehlt (K38).
        checkoutNavigation.assign(`/pricing?band=${encodeURIComponent(bandTierFromLookup(lookupKey))}`);
        return { redirected: true };
      }
      if (kind === 'special') {
        toast.error(checkoutText('errors.specialPlan'));
        return { redirected: false };
      }
      if (kind === 'rateLimited') {
        toast.error(checkoutText('errors.rateLimited'));
        return { redirected: false };
      }
      throw error;
    }
    if (data?.error) throw new Error(data.error);
    if (!data?.url) throw new Error('create-checkout returned no url');

    trackConversion('checkout_initiated', { plan: lookupKey, currency });
    checkoutNavigation.assign(data.url);
    return { redirected: true };
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: 'billing', action: 'checkout' } });
    toast.error(checkoutText('errors.generic'));
    return { redirected: false };
  }
};

/**
 * Oeffnet das Stripe-Kundenportal. Wirft bei Fehler UND wenn keine URL kommt
 * (vorher tat es dann still nichts und der Aufrufer-Spinner blieb stehen).
 */
export const openCustomerPortal = async () => {
  const { data, error } = await supabase.functions.invoke('customer-portal');
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  if (!data?.url) throw new Error('customer-portal returned no url');
  checkoutNavigation.assign(data.url);
};
